import { useState } from "react";
import { GripVertical, Plus, Trash2, ChevronUp, ChevronDown, Copy } from "lucide-react";
import { FIELD_TYPES, CHOICE_TYPES, DISPLAY_TYPES, TEXTAREA_TYPES, WIDTHS, SPAN_CLS, newField } from "./fieldTypes";

const inputCls = "w-full h-9 rounded-md border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-primary";

function FieldCard({ f, i, total, onPatch, onRemove, onMove, onDup, dragProps, dragging }) {
  const isDisplay = DISPLAY_TYPES.includes(f.type);
  return (
    <div {...dragProps} data-testid={`form-field-${i}`}
      className={`${SPAN_CLS[f.width] || SPAN_CLS[12]} rounded-lg border bg-white p-2.5 space-y-2 transition ${dragging ? "opacity-40 border-dashed" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center gap-1.5">
        <span className="cursor-grab text-slate-300 hover:text-slate-500 shrink-0" title="Drag to reorder" data-testid={`field-drag-${i}`}><GripVertical className="h-4 w-4" /></span>
        <select value={f.type} onChange={(e) => onPatch({ type: e.target.value })} data-testid={`field-type-${i}`}
          className="h-8 flex-1 min-w-0 rounded-md border border-slate-200 bg-slate-50 px-1.5 text-xs outline-none">
          {["Answer", "Choice", "Layout"].map((g) => (
            <optgroup key={g} label={g}>{FIELD_TYPES.filter((t) => t.group === g).map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</optgroup>
          ))}
        </select>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden shrink-0" data-testid={`field-width-${i}`} title="Width in the row">
          {WIDTHS.map((w) => (
            <button type="button" key={w.v} onClick={() => onPatch({ width: w.v })} data-testid={`field-width-${i}-${w.v}`}
              className={`h-8 px-1.5 text-[11px] font-semibold transition ${f.width === w.v ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{w.label}</button>
          ))}
        </div>
      </div>
      {TEXTAREA_TYPES.includes(f.type) ? (
        <textarea value={f.label} onChange={(e) => onPatch({ label: e.target.value })} rows={f.type === "statictext" ? 2 : 4} data-testid={`field-label-${i}`}
          placeholder={f.type === "statictext" ? "Instructions shown to parents…" : "One item per line — each line becomes a " + (f.type === "bullets" ? "bullet" : "numbered item")}
          className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-primary" />
      ) : (
        <input value={f.label} onChange={(e) => onPatch({ label: e.target.value })} data-testid={`field-label-${i}`}
          placeholder={f.type === "heading" ? "Section title" : `Question ${i + 1}`} className={inputCls} />
      )}
      {CHOICE_TYPES.includes(f.type) && (
        <input value={(f.options || []).join(", ")} onChange={(e) => onPatch({ options: e.target.value.split(",").map((s) => s.trimStart()) })}
          placeholder="Options, comma separated — e.g. Yes, No, Maybe" data-testid={`field-options-${i}`} className={inputCls} />
      )}
      {!isDisplay && (
        <input value={f.help || ""} onChange={(e) => onPatch({ help: e.target.value })} data-testid={`field-help-${i}`}
          placeholder={f.type === "file" ? "Help text — e.g. Attach 2 proofs of residence (PDF or photo)" : "Help text (optional)"}
          className="w-full h-8 rounded-md border border-slate-100 bg-slate-50 px-2.5 text-xs outline-none focus:border-primary" />
      )}
      <div className="flex items-center justify-between">
        {!isDisplay ? (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={!!f.required} onChange={(e) => onPatch({ required: e.target.checked })} data-testid={`field-required-${i}`} /> Required
          </label>
        ) : <span />}
        <div className="inline-flex items-center gap-0.5">
          <IconBtn onClick={() => onMove(-1)} disabled={i === 0} Icon={ChevronUp} label="Move up" testid={`field-up-${i}`} />
          <IconBtn onClick={() => onMove(1)} disabled={i === total - 1} Icon={ChevronDown} label="Move down" testid={`field-down-${i}`} />
          <IconBtn onClick={onDup} Icon={Copy} label="Duplicate" testid={`field-dup-${i}`} />
          <IconBtn onClick={onRemove} Icon={Trash2} label="Remove" testid={`field-remove-${i}`} danger />
        </div>
      </div>
    </div>
  );
}

function IconBtn({ onClick, Icon, label, testid, disabled, danger }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} data-testid={testid}
      className={`h-7 w-7 rounded-md inline-flex items-center justify-center transition disabled:opacity-30 ${danger ? "text-slate-400 hover:text-red-600 hover:bg-red-50" : "text-slate-400 hover:text-slate-900 hover:bg-slate-100"}`}>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export function FormBuilder({ fields, onChange }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [addType, setAddType] = useState("text");

  const patch = (i, p) => onChange(fields.map((f, j) => (j === i ? { ...f, ...p } : f)));
  const move = (from, to) => {
    if (to < 0 || to >= fields.length || from === to) return;
    const next = [...fields];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const dragProps = (i) => ({
    draggable: true,
    onDragStart: (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
    onDrop: (e) => { e.preventDefault(); if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); },
    onDragEnd: () => setDragIdx(null),
  });

  return (
    <div className="space-y-3" data-testid="form-builder">
      <div className="text-[11px] text-slate-500">
        Pick a width for each field (Full, ½, ⅓, ¼) — fields flow into the same row until it's full. Drag the grip or use the arrows to reorder.
      </div>
      <div className="grid grid-cols-12 gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-2 min-h-[80px]" data-testid="form-builder-grid">
        {fields.length === 0 && <div className="col-span-12 py-6 text-center text-sm text-slate-400">No fields yet — add one below.</div>}
        {fields.map((f, i) => (
          <FieldCard key={f._key || i} f={f} i={i} total={fields.length} dragging={dragIdx === i} dragProps={dragProps(i)}
            onPatch={(p) => patch(i, p)} onMove={(d) => move(i, i + d)}
            onRemove={() => onChange(fields.filter((_, j) => j !== i))}
            onDup={() => { const next = [...fields]; next.splice(i + 1, 0, { ...f, id: "", _key: Math.random().toString(36).slice(2) }); onChange(next); }} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={addType} onChange={(e) => setAddType(e.target.value)} data-testid="form-add-type" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm outline-none">
          {["Answer", "Choice", "Layout"].map((g) => (
            <optgroup key={g} label={g}>{FIELD_TYPES.filter((t) => t.group === g).map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</optgroup>
          ))}
        </select>
        <button type="button" onClick={() => onChange([...fields, { ...newField(addType), _key: Math.random().toString(36).slice(2) }])} data-testid="form-add-field"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition">
          <Plus className="h-3.5 w-3.5" /> Add field
        </button>
        <div className="flex-1" />
        {[["2 side by side", [6, 6]], ["3 in a row", [4, 4, 4]], ["4 in a row", [3, 3, 3, 3]]].map(([label, ws]) => (
          <button type="button" key={label} data-testid={`form-add-row-${ws.length}`}
            onClick={() => onChange([...fields, ...ws.map((w) => ({ ...newField("text"), width: w, _key: Math.random().toString(36).slice(2) }))])}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-[11px] font-medium text-slate-600 hover:bg-slate-100 transition">
            + {label}
          </button>
        ))}
      </div>
    </div>
  );
}
